import {
  type CSSProperties,
  createElement,
  type ReactElement,
  type ReactNode
} from 'react';
import type { Box } from 'v5/patterns/page-layout/marqueeGeometry';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

/** The keys of the items whose boxes meet `rect` (client coordinates), drawn or not. */
export type ItemsInRect = (rect: Box) => string[];

/**
 * How the tiles stand: the one thing each host picks for itself. The collection draws its list
 * with these classes and style; the tiles' drop line reads `--tile-collection-gap` to stay in the
 * gap's middle.
 */
export interface TileLayout {
  className: string;
  style?: CSSProperties & { '--tile-collection-gap'?: string };
  /** Each tile's box. */
  tileStyle?: CSSProperties;
  /** How the arrows move: across a grid, or up and down a stack. */
  arrows?: 'grid' | 'stack';
  /**
   * Around the list, e.g. a virtualizer; one that draws only what is on screen tells
   * `answersItemsInRect` where every item stands, so a rectangle picks those never drawn too.
   */
  wrap?(
    list: ReactElement,
    answersItemsInRect: (itemsInRect: ItemsInRect | null) => void
  ): ReactNode;
  /** The list takes the height its host gives and scrolls in it. */
  fills?: boolean;
  /**
   * The list grows into the free height of its host's flex column, as tall as its tiles at
   * least (a page around scrolls it): a drop below the last tile still lands on it.
   */
  grows?: boolean;
  /**
   * The drop line drawn in the box the layout gives each drop place (a virtualizer's), along a
   * column or across; without it the tiles draw it at their edge.
   */
  dropLine?: 'vertical' | 'horizontal';
}

/**
 * A fixed number of columns, or as many tiles at least `tileMinWidth` wide as fit, never wider
 * than the list. With no `padding`, 4px around the tiles holds their selection and focus rings
 * inside any scroller, the tiles still aligned with what sits above.
 */
export function gridLayout({
  tileMinWidth = '7.5rem',
  columns,
  gap,
  padding
}: {
  /** A CSS length. */
  tileMinWidth?: string;
  columns?: number;
  /** A CSS length; `--spacing(3)` without it. */
  gap?: string;
  /** A CSS padding around the tiles, in place of the ring room. */
  padding?: string;
}): TileLayout {
  return {
    className:
      padding === undefined
        ? 'relative grid gap-(--tile-collection-gap) -m-[4px] p-[4px] [--tile-collection-gap:--spacing(3)]'
        : 'relative grid gap-(--tile-collection-gap) [--tile-collection-gap:--spacing(3)]',
    style: {
      gridTemplateColumns:
        columns === undefined
          ? `repeat(auto-fill, minmax(min(100%, ${tileMinWidth}), 1fr))`
          : `repeat(${columns}, minmax(0, 1fr))`,
      ...(gap !== undefined && { '--tile-collection-gap': gap }),
      padding
    }
  };
}

/** One item a row, as wide as the list, the rows touching (each draws its own line): ↑↓ move. */
export function listLayout(): TileLayout {
  return {
    className:
      'relative flex flex-col gap-(--tile-collection-gap) [--tile-collection-gap:0px]',
    arrows: 'stack'
  };
}

/**
 * One row `height` high (padding included) that scrolls sideways, with soft edges and a button
 * at each edge scrolled past; every tile is as tall as the row, and as wide as its own content
 * makes it (a picture at its own proportion).
 */
export function stripLayout({
  height,
  gap,
  padding
}: {
  height: number;
  /** A CSS length. */
  gap: string;
  padding?: string;
}): TileLayout {
  return {
    className:
      'relative box-border flex w-full min-w-0 flex-nowrap items-stretch gap-(--tile-collection-gap) overflow-x-auto',
    style: { height, padding, '--tile-collection-gap': gap },
    tileStyle: { height: '100%', flexShrink: 0 },
    wrap: list =>
      createElement(ScrollShadowWrapper, {
        orientation: 'horizontal',
        edge: 'soft',
        asChild: true,
        scrollButtons: true,
        children: list
      })
  };
}
